// ─── CRM de TemeT (super admins) — capa de datos ─────────────────────────────
//
// Todo lo que el CRM lee o toca pasa por aquí. En producción son RPC de
// Supabase (SECURITY DEFINER con gate `_mezo_is_super_admin()`: el servidor
// es la autoridad, el email del front solo decide qué pantallas se muestran).
// En el mock de DESARROLLO (MOCK_DEV) devuelve fixtures de `crmMock.js` para
// poder diseñar y probar el CRM sin sesión real. OJO: MOCK_DEV no se enciende
// en el demo público — el CRM es interno.
//
// Se re-exporta directo desde `services/index.js` (patrón planesService): no
// pasa por `svc = USE_MOCK ? mockApi : realApi`, por eso cada función lleva
// su propia guarda.

import { supabase, MOCK_DEV } from './supabaseClient';
import * as mock from './crmMock';

async function rpc(nombre, args) {
  const { data, error } = await supabase.rpc(nombre, args);
  if (error) throw new Error(error.message || `Falló ${nombre}`);
  return data;
}

// ── Lecturas ────────────────────────────────────────────────────────────────
export const crmResumen  = () => (MOCK_DEV ? mock.resumen() : rpc('mezo_admin_crm_resumen'));
export const crmNegocios = () => (MOCK_DEV ? mock.negocios() : rpc('mezo_admin_crm_negocios'));
export const crmLeads    = () => (MOCK_DEV ? mock.leads() : rpc('mezo_admin_crm_leads'));
// Cuenta completa de un negocio. `desde`/`hasta` en ISO (YYYY-MM-DD, hora Colombia).
export const crmCuenta   = (businessId, desde, hasta) => (MOCK_DEV
  ? mock.cuenta(businessId, desde, hasta)
  : rpc('mezo_admin_cuenta', { p_business_id: businessId, p_desde: desde ?? null, p_hasta: hasta ?? null }));

// ── Notas CRM (historial) ───────────────────────────────────────────────────
export const TIPOS_NOTA_CRM = [
  { id: 'nota',     label: 'Nota' },
  { id: 'llamada',  label: 'Llamada' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'correo',   label: 'Correo' },
  { id: 'reunion',  label: 'Reunión' },
  { id: 'soporte',  label: 'Soporte' },
  { id: 'venta',    label: 'Venta' },
  { id: 'tarea',    label: 'Tarea' },
];
export const crmAgregarNota  = (businessId, texto, tipo = 'nota') => (MOCK_DEV
  ? mock.agregarNota(businessId, texto, tipo)
  : rpc('mezo_admin_crm_nota_agregar', { p_business_id: businessId, p_texto: texto, p_tipo: tipo }));
export const crmEliminarNota = (notaId) => (MOCK_DEV ? mock.eliminarNota(notaId) : rpc('mezo_admin_crm_nota_eliminar', { p_id: notaId }));

// ── Acciones sobre la cuenta ────────────────────────────────────────────────
export const crmActivarPlan = (businessId, plan, meses, motivo) => (MOCK_DEV
  ? mock.ok()
  : rpc('mezo_admin_activar_plan', { p_business_id: businessId, p_plan: plan, p_meses: meses, p_motivo: motivo ?? null }));
// Programa de referidos: si el negocio fue referido, premia al referidor.
// Devuelve { rewarded, referrer_nombre, referrer_email, meses } (rewarded=false
// si no había referido pendiente) o null si la RPC falla.
export const crmOtorgarReferido = (businessId) => (MOCK_DEV ? Promise.resolve(null) : rpc('mezo_otorgar_referido', { p_referred: businessId }).catch(() => null));
export const crmSetStatus = (businessId, status, motivo) => (MOCK_DEV
  ? mock.setStatus(businessId, status)
  : rpc('mezo_admin_set_status', { p_business_id: businessId, p_status: status, p_motivo: motivo ?? null }));
export const crmEliminarNegocio     = (businessId, motivo) => (MOCK_DEV ? mock.setStatus(businessId, 'eliminado') : rpc('mezo_admin_eliminar_negocio', { p_business_id: businessId, p_motivo: motivo ?? null }));
export const crmRestaurarNegocio    = (businessId) => (MOCK_DEV ? mock.setStatus(businessId, 'activo') : rpc('mezo_admin_restaurar_negocio', { p_business_id: businessId }));
export const crmQuitarRestricciones = (businessId) => (MOCK_DEV ? mock.setStatus(businessId, 'activo') : rpc('mezo_admin_quitar_restricciones', { p_business_id: businessId }));
export const crmResolverSolicitud   = (businessId, aprobar, meses = 0) => (MOCK_DEV ? mock.ok() : rpc('mezo_admin_resolver_solicitud', { p_business_id: businessId, p_aprobar: aprobar, p_meses: meses }));
// Nota interna "de una sola línea" (legado, businesses.admin_nota). Las notas
// con historial son crmAgregarNota.
export const crmSetNota = (businessId, nota) => (MOCK_DEV ? mock.ok() : rpc('mezo_admin_set_nota', { p_business_id: businessId, p_nota: nota }));
// Editar datos del negocio: { name, phone, telefono, email_contacto, nit,
// razon_social, city, ciudad, address, direccion_fiscal, sector,
// regimen_tributario, open_at, close_at } — solo lo que venga.
export const crmUpdateNegocio = (businessId, campos) => (MOCK_DEV ? mock.updateNegocio(businessId, campos) : rpc('mezo_admin_update_negocio', { p_business_id: businessId, p_campos: campos }));
export const crmCrearNegocio = ({ nombre, email, plan = null, meses = 1 }) => (MOCK_DEV
  ? mock.crearNegocio({ nombre, email, plan, meses })
  : rpc('mezo_admin_crear_negocio', { p_nombre: nombre, p_email: email, p_plan: plan, p_meses: meses }));

// Link mágico de acceso (flujo recovery de Supabase): huérfanos que no
// terminaron el registro, clientes sin contraseña, invitados.
export async function crmEnviarLinkAcceso(email) {
  if (MOCK_DEV) return mock.ok();
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: 'https://mezopos.com/login' });
  if (error) throw new Error(error.message);
  return { ok: true };
}

// Comunicado / promo a los negocios (edge function enviar-comunicado).
export async function crmEnviarComunicado(body) {
  if (MOCK_DEV) return { enviados: 3 };
  const { data, error } = await supabase.functions.invoke('enviar-comunicado', { body });
  if (error) throw new Error(error.message);
  // La función responde 200 con { error } si falta RESEND_API_KEY u otro fallo.
  if (data?.error) throw new Error(String(data.error));
  return data;
}
